import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDebouncedValue } from "../../../../hooks/useDebouncedValue";

type Props = {
	numericValue: number | undefined | null;
	setValueUpdate: (value: number) => void;
	maxDecimalPoints?: number;
};

/**
 * Custom hook to handle debounced input for a normalized float value.
 * The hook allow the user to type without restriction - however after debouncing the value will be validated and trimmed accordingly to the integer or max decimal points.
 * @param numericValue - The current numeric value in the outer state, which when changed will update the input value.
 * @param setValueUpdate - Function to update the numeric value in the outer state.
 * @param maxDecimalPoints - The number of max decimal points to allow in the input. Defaults to 2.
 * @returns An object containing the input change handler and the finalized numeric string value.
 */
export const useDebouncedNormalizedFloatInput = ({ numericValue, setValueUpdate, maxDecimalPoints = 2 }: Props) => {
	const [stringValue, setStringValue] = useState(numericValue ? numericValue.toFixed(Number.isInteger(numericValue) ? 0 : maxDecimalPoints) : "");
	const debouncedStringValue = useDebouncedValue(stringValue, 500);
	const didChangeSelfRef = useRef(true);

	const numericValueRef = useRef(numericValue);
	useEffect(() => {
		if (numericValue !== numericValueRef.current && typeof numericValue === "number") {
			numericValueRef.current = numericValue;
			const normalizedValue = numericValue.toFixed(Number.isInteger(numericValue) ? 0 : maxDecimalPoints);
			if (numericValue > 0) {
				setStringValue(normalizedValue);
			}
		}
	}, [numericValue, maxDecimalPoints]);

	useEffect(() => {
		if (didChangeSelfRef.current) {
			if (debouncedStringValue) {
				if (Number.isNaN(debouncedStringValue)) {
					setValueUpdate(0);
				} else {
					const normalizedValue = Number.parseFloat(debouncedStringValue);
					const finalizedValue = normalizedValue.toFixed(Number.isInteger(normalizedValue) ? 0 : maxDecimalPoints);
					setStringValue(finalizedValue);
					setValueUpdate(Number.parseFloat(finalizedValue));
				}
			} else {
				setValueUpdate(0);
			}
			didChangeSelfRef.current = false;
		}
	}, [debouncedStringValue, setValueUpdate, maxDecimalPoints]);

	const onInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const numericValue = Number.parseFloat(event.target.value);
		didChangeSelfRef.current = true;
		if (!Number.isNaN(numericValue) && numericValue >= 0) {
			setStringValue(event.target.value);
		} else {
			setStringValue("");
		}
	}, []);

	return {
		onInputChange,
		numberFinalizedValue: stringValue ?? "0",
	};
};
